/**
 * TrashcanFulfillmentBoard — Iter-194 · CS + Ops queue.
 *
 * Every trashcan provisioning request (NECS-provided at invite time)
 * lands here. CS/Ops sees a live queue with a one-tap
 * "Delegate to Ops · Same Day" action that stamps the row + fans an
 * in-app notification to Operations dispatch.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Trash2, Truck, CheckCircle, Zap, RefreshCw } from 'lucide-react';
import { roleTermFor } from '../lib/roleTerm';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrashcanFulfillmentBoard({ testIdPrefix = 'trashcan-board', showComplete = false }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, delegated: 0, delivered_all_time: 0, shown: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('active'); // 'active' | 'pending_delegation' | 'delegated' | 'all'
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'active' ? '' : `?status=${encodeURIComponent(filter)}`;
      const res = await fetch(`${API}/api/trashcan-requests${q}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not load trashcan queue.');
      const data = await res.json();
      setRows(data.requests || []);
      setCounts(data.counts || {});
    } catch (e) {
      if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function delegate(id) {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/trashcan-requests/${id}/delegate`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Delegation failed.');
      toast.success(`Delegated · Ops notified (${d.notified_ops_count || 0} dispatchers).`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  }

  async function complete(id) {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/trashcan-requests/${id}/complete`, {
        method: 'POST',
        credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Completion failed.');
      toast.success('Marked as delivered.');
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  }

  const chip = (label, value, active, onClick, testKey) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border transition ${
        active
          ? 'bg-[#FFD60A] text-black border-[#FFD60A]'
          : 'bg-zinc-950 text-[#C9A962] border-[#C9A962]/50 hover:bg-[#C9A962]/10'
      }`}
      data-testid={`${testIdPrefix}-filter-${testKey}`}
    >
      {label} · {value}
    </button>
  );

  return (
    <Card
      className="bg-zinc-900 border-2 border-[#FFD60A]/50 p-4 space-y-3"
      data-testid={testIdPrefix}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#FFD60A] flex items-center justify-center">
            <Trash2 className="h-5 w-5 text-black" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#C9A962]">CS + Ops Board</div>
            <div className="text-sm font-bold text-white">Trashcan Fulfillment · Same-Day Queue</div>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={load}
          disabled={loading}
          className="bg-zinc-800 text-[#FFD60A] hover:bg-zinc-700 border border-[#FFD60A]/40"
          data-testid={`${testIdPrefix}-refresh`}
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-filter-strip`}>
        {chip('Active', counts.pending + counts.delegated, filter === 'active', () => setFilter('active'), 'active')}
        {chip('Pending', counts.pending || 0, filter === 'pending_delegation', () => setFilter('pending_delegation'), 'pending')}
        {chip('Delegated', counts.delegated || 0, filter === 'delegated', () => setFilter('delegated'), 'delegated')}
        {chip('All', counts.delivered_all_time + counts.pending + counts.delegated, filter === 'all', () => setFilter('all'), 'all')}
      </div>

      {rows.length === 0 ? (
        <div className="p-6 rounded-lg border border-dashed border-zinc-700 text-center text-[12px] text-zinc-400" data-testid={`${testIdPrefix}-empty`}>
          {loading ? 'Loading trashcan queue…' : 'No trashcan requests in this bucket.'}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const isPending = r.status === 'pending_delegation';
            const isDelegated = r.status === 'delegated';
            const isDelivered = r.status === 'delivered';
            return (
              <div
                key={r.request_id}
                className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-3 space-y-2"
                data-testid={`${testIdPrefix}-row-${r.request_id}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Badge
                      className={`text-[10px] uppercase tracking-widest ${
                        isPending ? 'bg-amber-500 text-black'
                          : isDelegated ? 'bg-cyan-500 text-black'
                            : isDelivered ? 'bg-emerald-500 text-black'
                              : 'bg-zinc-700 text-white'
                      }`}
                      data-testid={`${testIdPrefix}-row-${r.request_id}-status`}
                    >
                      {(r.status || 'pending').replace(/_/g, ' ')}
                    </Badge>
                    <span className="font-mono text-[11px] text-zinc-400">{r.request_id}</span>
                    {r.same_day && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#FFD60A]">Same Day</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isPending && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => delegate(r.request_id)}
                        disabled={busyId === r.request_id}
                        className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
                        data-testid={`${testIdPrefix}-row-${r.request_id}-delegate`}
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        Delegate to Ops · Same Day
                      </Button>
                    )}
                    {showComplete && isDelegated && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => complete(r.request_id)}
                        disabled={busyId === r.request_id}
                        className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold"
                        data-testid={`${testIdPrefix}-row-${r.request_id}-complete`}
                      >
                        <CheckCircle className="h-3.5 w-3.5 mr-1" />
                        Mark Delivered
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-zinc-300">
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mr-1">Property</span>
                    <span className="text-white font-semibold">{r.property_name || '—'}</span>
                    {r.property_code && <span className="text-zinc-500 ml-1 font-mono text-[11px]">· {r.property_code}</span>}
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mr-1">Unit</span>
                    <span className="text-white font-semibold">{r.unit_number || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mr-1">{roleTermFor(r.property_type)}</span>
                    <span>{[r.invitee_first_name, r.invitee_last_name].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mr-1">Requested</span>
                    <span className="font-mono text-[11px] text-zinc-400">{(r.requested_at || '').slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  {r.delegated_by_name && (
                    <div className="sm:col-span-2">
                      <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mr-1"><Truck className="inline h-3 w-3 mr-0.5" />Delegated</span>
                      <span className="text-white">{r.delegated_by_name}</span>
                      <span className="text-zinc-500 ml-1 font-mono text-[11px]">· {(r.delegated_at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                  )}
                </div>
                {r.reason && <p className="text-xs text-zinc-300" data-testid={`${testIdPrefix}-reason-${r.request_id}`}>Reason: {r.reason} · {r.description || 'No additional notes'}</p>}
                {r.is_replacement && <p className="text-xs text-[#FFD60A]" data-testid={`${testIdPrefix}-fee-${r.request_id}`}>{isDelivered ? '$50 due · Recorded for Property Management on next monthly rent bill' : '$50 replacement fee · Not due until delivery'}</p>}
                {r.photos?.length > 0 && <details data-testid={`${testIdPrefix}-photos-${r.request_id}`}><summary className="text-xs text-zinc-300 cursor-pointer" data-testid={`${testIdPrefix}-photos-toggle-${r.request_id}`}>Photo evidence ({r.photos.length})</summary><div className="grid grid-cols-3 gap-2 mt-2">{r.photos.map((p, i) => <img key={i} alt={`Trashcan evidence ${i + 1}`} src={p.data || p.data_url || p.url} className="w-full aspect-square object-contain" data-testid={`${testIdPrefix}-photo-${r.request_id}-${i}`} />)}</div></details>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
